export default class GroupsButtonService {
  constructor() {
    this.cache = {
      editState: false,
      totalGroupsSelected: 0,
      groupTree: [],
      groupsRef: {},
      groupsSeen: {},
      selectedGroupsTree: []
    };
  }

  /**
   * Recurses through the tree-like array of objects and creates a key/value pair for every one
   * encountered in the tree inside the vm.groupRef object.
   *
   * @param {Array<object>} grps A deep tree-like array of objects
   * @returns {boolean} (True) A boolean for the sake of triggering the next promise -Devin
   */
  flattenTheGroupsIntoReferralObj(grps) {
    this.cache.groupsRef = {};
    return this._flattenTheGroupsIntoReferralObjRe(grps);
  }

  _flattenTheGroupsIntoReferralObjRe(grps) {
    grps = angular.copy(grps);
    let i = 0;
    while (i < grps.length) {
      const gp = grps[i];
      this.cache.groupsRef[gp.id] = gp;
      if (gp.subGroups?.length > 0) {
        this._flattenTheGroupsIntoReferralObjRe(gp.subGroups);
      }
      i += 1;
    }
    return true;
  }

  /**
   * The opposite of the flattenTheGroups... method, this builds a tree-like array starting
   * from the first grp object passed in and works its way up using the parent property until
   * it reaches the highest object the user is authorized to access, which it then pushes into the selectedGroupsTree.
   *
   * @param {object} grp - the current group object in focus
   * @param {object} childGrp - the grp object's child object
   */
  buildSelectedTree(grp, childGrp) {
    const cache = this.cache;
    if (cache.groupsSeen[grp.id]) {
      cache.groupsSeen[grp.id] += 1;
    } else {
      cache.totalGroupsSelected += 1;
      cache.groupsSeen[grp.id] = 1;
      grp.subGroups = [];
    }

    if (childGrp && !_.includes(grp.subGroups, childGrp)) {
      grp.subGroups.push(childGrp);
    }
    const parent = _.get(cache, ['groupsRef', grp.parent]);
    if (parent) {
      this.buildSelectedTree(parent, grp);
    } else if (cache.groupsSeen[grp.id] === 1) {
      cache.selectedGroupsTree.push(grp);
    }
  }

  /**
   * Builds the tree downside from the root (group) selected.
   *
   * @param {object} root - the current group root
   * @param {object} grp - the current group object in focus
   * @param {object} childGrp - the grp object's child object
   */
  buildSelectedSubtree(root, grp, childGrp) {
    const cache = this.cache;
    if (cache.groupsSeen[grp.id]) {
      cache.groupsSeen[grp.id] += 1;
    } else {
      cache.totalGroupsSelected += 1;
      cache.groupsSeen[grp.id] = 1;
      grp.subGroups = [];
    }

    if (childGrp && !_.includes(grp.subGroups, childGrp)) {
      grp.subGroups.push(childGrp);
    }
    const parent = _.get(cache, ['groupsRef', grp.parent]);
    if (parent && root.id !== grp.id) {
      this.buildSelectedSubtree(root, parent, grp);
    } else if (cache.groupsSeen[grp.id] === 1) {
      cache.selectedGroupsTree.push(grp);
    }
  }

  buildSelectedTreeWithChilds(root, grp) {
    const subGroups = _.filter(this.cache.groupsRef, subGroup => grp.id === subGroup.parent);
    if (subGroups && subGroups.length > 0) {
      _.forEach(subGroups, (group) => {
        const indexInSelected = _.indexOf(this.cache.selectedGroupsTree, group);
        if (indexInSelected > -1) {
          this.cache.selectedGroupsTree.splice(indexInSelected, 1);
        }
        this.buildSelectedTreeWithChilds(root, group);
      });
    } else {
      this.buildSelectedSubtree(root, grp);
    }
  }

  selectUnseenGroup(grp, multiChildSelection) {
    if (!this.cache.groupsSeen[grp.id] || multiChildSelection) {
      if (multiChildSelection) {
        this.buildSelectedTreeWithChilds(this.cache.groupsRef[grp.id], this.cache.groupsRef[grp.id]);
      } else {
        this.buildSelectedTree(this.cache.groupsRef[grp.id]);
      }
    }
  }

  /**
   * Upon click, accepts the target item object, a group. This grp's reference is spliced from its parent,
   * if applicable.
   *
   * @param {object} grp - the item object passed from the specific GroupsTreeDirective originating the click event
   */
  removeGroupFromTree(grp) {
    const cache = this.cache;
    cache.groupsRef[grp.id].subGroups = [];
    const parentSubGroups = _.get(cache, ['groupsRef', grp.parent, 'subGroups']);
    const indexInParent = _.findIndex(parentSubGroups, ['id', grp.id]);
    if (indexInParent > -1) {
      parentSubGroups.splice(indexInParent, 1);
    }
    const indexInSelected = _.findIndex(cache.selectedGroupsTree, ['id', grp.id]);
    if (indexInSelected > -1) {
      cache.selectedGroupsTree.splice(_.indexOf(cache.selectedGroupsTree, cache.groupsRef[grp.id]), 1);
    }
  }

  /**
   * recursively removes the grp and its children from the groupsSeen object while reducing the totalGroupsSelected
   * by one for each removed.
   *
   * @param {object} grp - the item object passed from the specific GroupsTreeDirective originating the click event
   * @returns {undefined}
   */
  resetCount(grp) {
    this.cache.totalGroupsSelected -= 1;
    delete this.cache.groupsSeen[grp.id];
    _.forEach(grp.subGroups, sg => this.resetCount(sg));
  }

  /**
   * Creates a new selectedGroups array with the necessary structure for submitting to server.
   *
   * @param {Array} [temp=[]] Can pass array as reference or use result value
   * @param {object} multiChildSelection
   * @returns {Array<object>} Groups
   */
  rebuildSelectedGroups(temp = [], multiChildSelection = false) {
    const cache = this.cache;
    // If it has multiChildSelection , root of the tree will be from the selected group
    const seenKeys = multiChildSelection ? _.map(cache.selectedGroupsTree, 'id') : _.keys(cache.groupsSeen);
    const size = _.size(seenKeys);
    let i = 0;
    while (i < size) {
      temp.push({
        id: cache.groupsRef[seenKeys[i]].id,
        name: cache.groupsRef[seenKeys[i]].name
      });
      i += 1;
    }
    return temp;
  }

  cleanCache() {
    this.cache.totalGroupsSelected = 0;
    this.cache.editState = false;
    this.cache.groupTree = [];
    this.cache.groupsRef = {};
    this.cache.groupsSeen = {};
    this.cache.selectedGroupsTree = [];
  }
}
